import { useCallback, useEffect, useRef, useState } from 'react';
import { useTimeout } from '@mantine/hooks';

const DEFAULT_HOVER_DURATION = 320;
const DEFAULT_ACTIVE_DURATION = 250;
const DEFAULT_APPEAR_DELAY = 50;

const assignTarget = (
  items: Map<string, HTMLElement>,
  id: string | null | undefined,
  setTarget: (el: HTMLElement | null) => void,
) => {
  if (!id) {
    setTarget(null);
    return;
  }

  setTarget(items.get(id) ?? null);
};

const useSkipFirstTransition = (
  active: boolean,
  duration: number,
  delay: number,
) => {
  const [transitionDuration, setTransitionDuration] = useState(0);
  const { start, clear } = useTimeout(
    () => setTransitionDuration(duration),
    delay,
  );

  useEffect(() => {
    if (!active) {
      clear();
      setTransitionDuration(0);
      return;
    }

    start();
    return clear;
  }, [active, clear, start]);

  return transitionDuration;
};

export type UseFloatingRowIndicatorOptions = {
  activeId?: string | null;
  enabled?: boolean;
  hoverDuration?: number;
  activeDuration?: number;
  appearDelay?: number;
};

export const useFloatingRowIndicator = ({
  activeId,
  enabled = true,
  hoverDuration = DEFAULT_HOVER_DURATION,
  activeDuration = DEFAULT_ACTIVE_DURATION,
  appearDelay = DEFAULT_APPEAR_DELAY,
}: UseFloatingRowIndicatorOptions = {}) => {
  const items = useRef(new Map<string, HTMLElement>());
  const activeIdRef = useRef(activeId);
  const hoveredIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;

  const [parent, setParent] = useState<HTMLElement | null>(null);
  const [activeTarget, setActiveTarget] = useState<HTMLElement | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [hoverTarget, setHoverTarget] = useState<HTMLElement | null>(null);
  hoveredIdRef.current = hoveredId;

  const hoverTransitionDuration = useSkipFirstTransition(
    Boolean(hoverTarget),
    hoverDuration,
    appearDelay,
  );
  const activeTransitionDuration = useSkipFirstTransition(
    Boolean(activeTarget),
    activeDuration,
    appearDelay,
  );

  const registerItem = useCallback(
    (id: string, el: HTMLElement | null) => {
      if (!enabled) return;

      if (el) items.current.set(id, el);
      else items.current.delete(id);

      if (id === activeIdRef.current) setActiveTarget(el);
      if (id === hoveredIdRef.current) setHoverTarget(el);
    },
    [enabled],
  );

  useEffect(() => {
    assignTarget(items.current, enabled ? activeId : null, setActiveTarget);
    if (!enabled) {
      hoveredIdRef.current = null;
      setHoveredId(null);
      setHoverTarget(null);
    }
  }, [activeId, enabled]);

  const onItemHover = useCallback(
    (id: string) => {
      if (!enabled || id === hoveredIdRef.current) return;
      hoveredIdRef.current = id;
      setHoveredId(id);
      assignTarget(items.current, id, setHoverTarget);
    },
    [enabled],
  );

  const onMouseLeave = useCallback(() => {
    hoveredIdRef.current = null;
    setHoveredId(null);
    setHoverTarget(null);
  }, []);

  return {
    parent,
    setParent,
    registerItem,
    onItemHover,
    onMouseLeave,
    hoveredId,
    hoverTarget,
    activeTarget,
    hoverTransitionDuration,
    activeTransitionDuration,
  };
};